import React, { useEffect, useMemo, useRef, useState } from "react";
import { Clock3, LogOut } from "lucide-react";
import Litepicker from "@/components/Base/Litepicker";
import Lucide from "@/components/Base/Lucide";
import { useNavigate } from "react-router-dom";
import PageLoadingOverlay from "@/components/PageLoadingOverlay";

function resolveApiUrl() {
  const envApiUrl = import.meta.env.VITE_API_URL as string | undefined;
  const fallback = `${window.location.protocol}//${window.location.hostname}:4000`;
  if (!envApiUrl) return fallback;
  try {
    const u = new URL(envApiUrl);
    if (u.hostname === "localhost" || u.hostname === "127.0.0.1") return fallback;
    return envApiUrl;
  } catch {
    return envApiUrl || fallback;
  }
}

type ActivityItem = {
  id: string;
  type: string;
  title: string;
  details: string | null;
  statusBadge: string | null;
  statusType: string | null;
  timeLabel: string;
  createdAt: string;
  imageUrl: string | null;
  relatedId: string | null;
  relatedType: string | null;
};

type ActivityGroup = {
  date: string;
  items: ActivityItem[];
};

type ActivityResponse = {
  groups: ActivityGroup[];
  hasMore: boolean;
  nextCursorDate: string | null;
};

function formatDateHeading(dateKey: string) {
  const [y, m, d] = dateKey.split("-").map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
}

function statusBadgeClass(statusType?: string | null) {
  if (statusType === "success") return "bg-green-100 text-green-700";
  if (statusType === "warning") return "bg-amber-100 text-amber-700";
  if (statusType === "info") return "bg-blue-100 text-blue-700";
  return "bg-primary/10 text-primary";
}

function shouldShowThumbnail(item: ActivityItem) {
  return Boolean(item.imageUrl) && item.title.trim().toLowerCase() !== "submitted sales";
}

export default function Activity() {
  const navigate = useNavigate();
  const apiUrl = useMemo(() => resolveApiUrl(), []);
  const token = localStorage.getItem("token") || "";
  const todayDateKey = useMemo(() => new Date().toISOString().slice(0, 10), []);

  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [groups, setGroups] = useState<ActivityGroup[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursorDate, setNextCursorDate] = useState<string | null>(null);

  const [startDateInput, setStartDateInput] = useState(todayDateKey);
  const [endDateInput, setEndDateInput] = useState(todayDateKey);
  const [appliedStartDate, setAppliedStartDate] = useState("");
  const [appliedEndDate, setAppliedEndDate] = useState("");
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);

  const sentinelRef = useRef<HTMLDivElement | null>(null);

  const fetchActivities = async (append: boolean, cursorDate?: string | null) => {
      if (!token) {
        setError("Not authenticated. Please login again.");
        return;
      }

      if (append) setLoadingMore(true);
      else {
        setLoading(true);
        setError("");
      }

      try {
        const params = new URLSearchParams();
        params.set("limitDates", "5");
        if (append && cursorDate) params.set("cursorDate", cursorDate);
        if (appliedStartDate) params.set("startDate", appliedStartDate);
        if (appliedEndDate) params.set("endDate", appliedEndDate);

        const res = await fetch(`${apiUrl}/supervisor/activities?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const json: ActivityResponse | any = await res.json().catch(() => ({}));
        if (!res.ok) {
          setError(json?.message || `Failed to load activities (${res.status}).`);
          return;
        }

        setGroups((prev) => (append ? [...prev, ...(json.groups ?? [])] : json.groups ?? []));
        setHasMore(Boolean(json.hasMore));
        setNextCursorDate(json.nextCursorDate ?? null);
      } catch {
        setError("Unable to connect to server. Please try again.");
      } finally {
        if (append) setLoadingMore(false);
        else setLoading(false);
      }
    };

  useEffect(() => {
    setNextCursorDate(null);
    void fetchActivities(false, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiUrl, token, appliedStartDate, appliedEndDate]);

  useEffect(() => {
    if (!sentinelRef.current || !hasMore || loadingMore || loading) return;
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry?.isIntersecting && hasMore && !loadingMore && !loading) {
          void fetchActivities(true, nextCursorDate);
        }
      },
      { root: null, rootMargin: "160px 0px", threshold: 0.1 }
    );
    observer.observe(sentinelRef.current);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, loadingMore, loading, nextCursorDate, appliedStartDate, appliedEndDate]);

  const applyDateFilter = () => {
    setAppliedStartDate(startDateInput.trim());
    setAppliedEndDate(endDateInput.trim());
    setNextCursorDate(null);
  };

  const clearDateFilter = () => {
    setStartDateInput(todayDateKey);
    setEndDateInput(todayDateKey);
    setAppliedStartDate("");
    setAppliedEndDate("");
    setNextCursorDate(null);
  };

  const handleLogout = async () => {
    try {
      if (token) {
        await fetch(`${apiUrl}/supervisor/logout`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        });
      }
    } catch {
      // Ignore logout logging failures and continue local logout.
    }
    localStorage.removeItem("token");
    localStorage.removeItem("role");
    localStorage.removeItem("userFirstName");
    localStorage.removeItem("userLastName");
    localStorage.removeItem("userTeamName");
    sessionStorage.clear();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-[calc(100vh-60px)] bg-white">
      <PageLoadingOverlay show={loading} label="Loading activities…" />
      <div className="h-[102px] bg-gradient-to-r from-theme-1/95 to-theme-2/95 text-white relative">
        <div aria-hidden className="absolute inset-0 bg-texture-white opacity-20 pointer-events-none" />
        <div className="relative z-10 h-full px-4 pt-4">
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <div className="text-xs font-normal opacity-90 truncate">Activity</div>
              <div className="text-base font-bold mt-1 truncate">User Activity Logs</div>
            </div>
            <button
              type="button"
              onClick={() => void handleLogout()}
              className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center"
              aria-label="Logout"
            >
              <LogOut size={20} className="text-white" />
            </button>
          </div>
        </div>
      </div>

      <div className="px-4 pt-3 pb-2 bg-white border-b border-gray-100">
        <div className="rounded-xl border border-gray-200 bg-white shadow-[0_2px_12px_rgba(0,0,0,0.08)] p-3">
          <div className="text-sm font-semibold text-slate-900 mb-2">Search Date Range</div>
          <div className="grid grid-cols-2 gap-2">
            <div className="relative">
              <div className="absolute flex items-center justify-center w-10 h-full border rounded-l bg-slate-100 text-slate-500 dark:bg-darkmode-700 dark:border-darkmode-800 dark:text-slate-400">
                <Lucide icon="Calendar" className="w-4 h-4" />
              </div>
              <Litepicker
                value={startDateInput}
                onChange={(e) => setStartDateInput(e.target.value)}
                options={{
                  autoApply: true,
                  format: "YYYY-MM-DD",
                  dropdowns: { months: true, years: true },
                }}
                className="pl-12"
              />
            </div>
            <div className="relative">
              <div className="absolute flex items-center justify-center w-10 h-full border rounded-l bg-slate-100 text-slate-500 dark:bg-darkmode-700 dark:border-darkmode-800 dark:text-slate-400">
                <Lucide icon="Calendar" className="w-4 h-4" />
              </div>
              <Litepicker
                value={endDateInput}
                onChange={(e) => setEndDateInput(e.target.value)}
                options={{
                  autoApply: true,
                  format: "YYYY-MM-DD",
                  dropdowns: { months: true, years: true },
                }}
                className="pl-12"
              />
            </div>
          </div>
          <div className="mt-2 grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={applyDateFilter}
              disabled={loading}
              className="rounded-lg bg-primary text-white text-sm font-semibold py-2 disabled:opacity-60"
            >
              Apply
            </button>
            <button
              type="button"
              onClick={clearDateFilter}
              className="rounded-lg border border-gray-200 bg-white text-slate-700 text-sm font-semibold py-2"
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      <div className="px-4 pt-3 pb-6 bg-white">
        {error ? <div className="rounded-lg border border-red-200 bg-red-50 text-red-700 px-3 py-2 text-sm">{error}</div> : null}

        {!loading && !error && groups.length === 0 ? (
          <div className="rounded-lg border border-gray-200 bg-gray-50 text-slate-600 px-3 py-3 text-sm">
            No activity found for the selected date range.
          </div>
        ) : null}

        {!loading && !error ? (
          <div className="space-y-3">
            {groups.map((group) => (
              <div
                key={group.date}
                className="rounded-xl border border-gray-200 bg-white shadow-[0_2px_12px_rgba(0,0,0,0.08)] overflow-hidden"
              >
                <div className="px-3 py-2 border-b border-gray-100 bg-primary/5">
                  <div className="text-sm font-semibold text-slate-900">{formatDateHeading(group.date)}</div>
                </div>

                <div className="px-3 py-1">
                  {group.items.map((item) => (
                    <div key={item.id} className="py-2 border-b border-gray-100 last:border-b-0">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold text-slate-900">{item.title}</div>
                          {item.details ? <div className="text-xs text-slate-600 mt-0.5">{item.details}</div> : null}
                        </div>
                        {item.statusBadge ? (
                          <div className={`text-[11px] font-semibold px-2 py-1 rounded-full ${statusBadgeClass(item.statusType)}`}>
                            {item.statusBadge}
                          </div>
                        ) : null}
                      </div>

                      <div className="mt-1 flex items-center justify-between gap-2">
                        <div className="inline-flex items-center gap-1 text-[11px] text-slate-500">
                          <Clock3 size={12} />
                          <span>{item.timeLabel}</span>
                        </div>
                        {shouldShowThumbnail(item) ? (
                          <button
                            type="button"
                            onClick={() => setPreviewImageUrl(item.imageUrl!)}
                            className="rounded-md"
                            aria-label="Open activity image preview"
                          >
                            <img
                              src={item.imageUrl!}
                              alt="Activity thumbnail"
                              className="w-12 h-12 rounded-md border border-gray-200 object-cover bg-gray-50"
                              loading="lazy"
                            />
                          </button>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <div ref={sentinelRef} />
            {loadingMore ? <div className="text-xs text-slate-500 py-1">Loading more dates...</div> : null}
            {!hasMore && groups.length > 0 ? (
              <div className="text-xs text-slate-400 py-1">No more activity dates.</div>
            ) : null}
          </div>
        ) : null}
      </div>

      {previewImageUrl ? (
        <button
          type="button"
          className="fixed inset-0 z-[120] bg-black/75 p-4 flex items-start justify-center"
          onClick={() => setPreviewImageUrl(null)}
          aria-label="Close image preview"
        >
          <div
            className="relative w-[70vw] h-[70vh] max-w-[480px] max-h-[700px] rounded-lg bg-white p-2"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setPreviewImageUrl(null)}
              className="absolute top-2 right-2 z-10 rounded-full bg-black/70 text-white w-8 h-8 flex items-center justify-center text-sm"
              aria-label="Close image preview"
            >
              X
            </button>
            <img
              src={previewImageUrl}
              alt="Full activity preview"
              className="w-full h-full object-contain rounded-md bg-white"
            />
          </div>
        </button>
      ) : null}
    </div>
  );
}

